import { useCallback, useEffect, useMemo, useState } from "react";
import { CheckCheck, Search } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Money } from "@/components/Money";
import { PageHeader } from "@/components/PageHeader";
import api, { apiError } from "@/lib/api";

export default function ExpenseMappingPage() {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const load = useCallback(() => api.get("/erp/accounts").then((r) => setData(r.data)).catch((e) => toast.error(apiError(e))), []);
  useEffect(() => { load(); }, [load]);

  const rows = useMemo(() => {
    const s = q.trim().toLowerCase();
    return (data?.accounts || []).filter((a) => !s || [a.account, a.description, a.fs_group, a.sub_group].some((v) => (v || "").toLowerCase().includes(s)));
  }, [data, q]);

  const change = async (account, category) => {
    try {
      await api.put(`/erp/accounts/${account}`, { category });
      toast.success("Mapping updated — P&L figures recalculated");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const reviewAll = async () => { await api.post("/erp/accounts/mark-reviewed"); load(); };
  const newCount = (data?.accounts || []).filter((a) => a.is_new).length;

  return (
    <div className="space-y-6" data-testid="expense-mapping-page">
      <PageHeader eyebrow="Data" title="Expense mapping" subtitle="Each expense account maps to one P&L category. Changes apply to all dashboards, reports and exports immediately."
        actions={newCount > 0 && <Button variant="outline" onClick={reviewAll} className="gap-1.5 bg-white" data-testid="mapping-mark-reviewed"><CheckCheck className="h-4 w-4" /> Mark {newCount} new as reviewed</Button>} />
      <div className="relative w-full max-w-xs">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search accounts…" className="bg-white pl-9" data-testid="mapping-search" />
      </div>
      <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white shadow-sm">
        <table className="w-full text-sm" data-testid="mapping-table">
          <thead className="bg-slate-50 text-xs font-semibold uppercase tracking-wider text-slate-500">
            <tr>{["Account", "Description", "FS Group", "Sub FS Group", "Sec.", "Total (all months)", "P&L category"].map((h) => <th key={h} className={`whitespace-nowrap px-4 py-3 ${h.startsWith("Total") ? "text-right" : "text-left"}`}>{h}</th>)}</tr>
          </thead>
          <tbody>
            {rows.map((a) => (
              <tr key={a.account} className="row-hover border-t border-slate-100" data-testid={`mapping-row-${a.account}`}>
                <td className="num whitespace-nowrap px-4 py-2 text-slate-700">{a.account}{a.is_new && <span className="ml-2 rounded bg-teal-50 px-1.5 py-0.5 font-sans text-[10px] font-semibold uppercase text-teal-700" data-testid={`mapping-new-${a.account}`}>New</span>}</td>
                <td className="px-4 py-2 text-slate-800">{a.description}</td>
                <td className="px-4 py-2 text-xs text-slate-600">{a.fs_group}</td>
                <td className="px-4 py-2 text-xs text-slate-600">{a.sub_group}</td>
                <td className="px-4 py-2 text-xs text-slate-500">{a.section}</td>
                <td className="px-4 py-2 text-right"><Money value={a.total} /></td>
                <td className="px-4 py-2">
                  {(
                    <Select value={a.category} onValueChange={(v) => change(a.account, v)}>
                      <SelectTrigger className="h-8 w-56 text-xs" data-testid={`mapping-select-${a.account}`}><SelectValue /></SelectTrigger>
                      <SelectContent>{data.categories.map((c) => <SelectItem key={c.key} value={c.key} className="text-xs" data-testid={`mapping-option-${c.key}`}>{c.label}</SelectItem>)}</SelectContent>
                    </Select>
                  )}
                </td>
              </tr>
            ))}
            {data && rows.length === 0 && <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-500">No expense accounts yet — upload an Expenses report.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
